import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { isDotCom } from '@src/site';
import { LANDING_CONFIGS } from '@src/pages/LandingPage/configs';
import { normalizePath, parseBoxPath } from '@src/routing/routes';
import { currentBoxSubdomain } from '@src/routing/boxDomain';
import { useHydrated } from '@src/utils/useHydrated';
import { useAppSelector } from '@src/state/hooks';
import { santaClient } from '@src/state/client';
import { cleanupAll, loadAdScript, loadVideoAds, syncVideoShadowStyles } from './loader';
import { useMatchesMedia, MOBILE_HEADER_MQ } from '@src/components/layout/useMobileHeaderRecede';
import { Container } from '@src/components/Container/Container';
import { hasAdFreeAccess, isOrganizerManagementPage } from './eligibility';
import './Ads.css';

const AdContext = createContext({ enabled: false, previewAllowed: false });
const FULL_INLINE_AD_PATHS = new Set([
  '/',
  '/faq',
  '/randomizer',
  '/policy',
  '/privacy_policy',
  '/account/boxes',
  '/box/new',
  '/login',
  '/register',
  '/recover',
  ...LANDING_CONFIGS.map(({ path }) => path),
]);
export function adsAllowedHost(host: string) {
  return host === 'santa-secret.ru' || host === 'www.santa-secret.ru';
}
export function AdsProvider({ children }: { children: ReactNode }) {
  const session = useAppSelector((state) => state.session);
  const location = useLocation();
  const mobile = useMatchesMedia(MOBILE_HEADER_MQ);
  const mobileCreation = mobile && normalizePath(location.pathname) === '/box/new';
  const boxInfo = useAppSelector((state) => state.box.info);
  const boxRoute = parseBoxPath(location.pathname);
  const boxKey = boxRoute?.key ?? null;
  const organizerManagement = isOrganizerManagementPage(boxRoute, boxInfo);
  const [boxPermission, setBoxPermission] = useState<{ key: string; adsDisabled: boolean } | null>(
    null,
  );
  const adFreeUser = hasAdFreeAccess(session.user);
  const boxAllowsAds =
    !boxKey ||
    (boxInfo?.box.key === boxKey
      ? !hasAdFreeAccess(boxInfo.box)
      : boxPermission?.key === boxKey && !boxPermission.adsDisabled);
  const [permission, setPermission] = useState<{ userId: number | null; allowed: boolean } | null>(
    null,
  );
  const gameDomain = Boolean(window.__ssBoxKey || currentBoxSubdomain());
  const eligible = !organizerManagement && !mobileCreation && !gameDomain && !isDotCom() && adsAllowedHost(window.location.hostname);
  const canDisplay = !organizerManagement && !mobileCreation && session.status === 'ready' && !adFreeUser && boxAllowsAds && !gameDomain;
  const allowed =
    eligible &&
    canDisplay &&
    permission?.userId === (session.user?.id ?? null) &&
    permission.allowed;
  useEffect(() => {
    if (!eligible || session.status !== 'ready' || adFreeUser) return;
    let disposed = false;
    void (async () => {
      const result = session.user ? await santaClient.getIsDonated() : { ok: true, data: false };
      if (!disposed)
        setPermission({ userId: session.user?.id ?? null, allowed: result.ok && !result.data });
    })().catch(() => {
      /* Leave ads off when donor status cannot be checked. */
    });
    return () => {
      disposed = true;
    };
  }, [eligible, session.status, session.user, adFreeUser]);
  useEffect(() => {
    if (!boxKey || gameDomain || boxInfo?.box.key === boxKey) return;
    let active = true;
    void santaClient
      .getBoxAppearance({ key: boxKey })
      .then((result) => {
        if (active && result.ok && result.data)
          setBoxPermission({ key: boxKey, adsDisabled: hasAdFreeAccess(result.data) });
      })
      .catch(() => {
        /* Leave ads off when box entitlement cannot be checked. */
      });
    return () => {
      active = false;
    };
  }, [boxKey, gameDomain, boxInfo?.box.key]);
  const enabled = Boolean(allowed && !location.pathname.startsWith('/open'));
  useEffect(() => {
    document.body.classList.toggle('ads-disabled', !enabled);
    if (!enabled) cleanupAll();
    return () => document.body.classList.remove('ads-disabled');
  }, [enabled]);
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const timer = setTimeout(() => void loadVideoAds(controller.signal), 1000);
    // Providers may attach their shadow root after the script's load event.
    const shadowTimer = setInterval(syncVideoShadowStyles, 1000);
    return () => {
      clearTimeout(timer);
      clearInterval(shadowTimer);
      controller.abort();
    };
  }, [enabled]);
  return (
    <AdContext.Provider value={{ enabled, previewAllowed: Boolean(canDisplay && !isDotCom()) }}>
      {children}
    </AdContext.Provider>
  );
}

/** Legacy inline placements, kept out of focused mobile creation flows. */
export function InlineAds({ placement = false }: { placement?: boolean } = {}) {
  const hydrated = useHydrated();
  const preview =
    hydrated &&
    !window.__ssBoxKey &&
    !currentBoxSubdomain() &&
    ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
  const { enabled, previewAllowed } = useContext(AdContext);
  const { pathname } = useLocation();
  const id = useId().replace(/[^a-zA-Z0-9]/g, '');
  const mobile = useMatchesMedia(MOBILE_HEADER_MQ);
  const placementsRef = useRef<HTMLDivElement>(null);
  const [desktopCount, setDesktopCount] = useState(4);
  const showPlacement = placement || FULL_INLINE_AD_PATHS.has(normalizePath(pathname));
  const visible = (enabled || (preview && previewAllowed)) && showPlacement;
  const count = visible ? (mobile ? 2 : desktopCount) : 0;
  useEffect(() => {
    const element = placementsRef.current;
    if (!element) return;
    // Every placement uses the same 300px cards and 16px gap, up to four columns.
    const observer = new ResizeObserver(([entry]) => {
      setDesktopCount(Math.max(2, Math.min(4, Math.floor((entry.contentRect.width + 16) / 316))));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [visible]);
  useEffect(() => {
    if (!enabled || !count) return;
    let disposed = false;
    const instances: { destroy?: () => void }[] = [];
    const timer = setTimeout(() => {
      void loadAdScript('https://sdk.adlook.tech/inventory/core.js', 'head')
        .then(() => {
          if (disposed || !window.UTInventoryCore) return;
          for (let index = 0; index < count; index++) {
            instances.push(
              new window.UTInventoryCore({
                type: 'embed',
                host: 724,
                content: false,
                container: `ad-${id}-${index}`,
                width: 400,
                height: 225,
                playMode: 'autoplay-visible',
                collapse: 'none',
                infinity: true,
                infinityTimer: 1,
                withoutIframe: true,
              }),
            );
          }
        })
        .catch(() => {
          /* Ad blockers do not affect the page. */
        });
    }, 1000);
    return () => {
      disposed = true;
      clearTimeout(timer);
      instances.forEach((instance) => instance?.destroy?.());
    };
  }, [enabled, count, id, pathname]);
  if ((!enabled && !preview) || !count) return null;
  return (
    <Container
      width="content"
      gutter="default"
      className={`inline-ads${count > 1 ? ' inline-ads--multiple' : ''}${
        preview ? ' inline-ads--preview' : ''
      }`}
      testId="inline-ads"
    >
      <div ref={placementsRef} className="inline-ads__placements">
        {Array.from({ length: count }, (_, index) => (
          <div key={index} id={`ad-${id}-${index}`} className="inline-ads__slot">
            {preview && (
              <div className="inline-ads__placeholder" data-testid="adlook-placeholder">
                <strong>AdLook · {index + 1}</strong>
                <span>Ad placement preview</span>
                <small>Localhost only · no live ads</small>
              </div>
            )}
          </div>
        ))}
      </div>
    </Container>
  );
}
